import React, { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Link,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  Divider,
  Grid,
} from '@mui/material';
import { getApplicationByApplicantToken, updateApplicationByApplicantToken } from '../services/rentalApplicationService';
import type { RentalApplicationSummary } from '../services/rentalApplicationService';

function formatDisplayDate(isoDate: string | undefined): string {
  if (!isoDate) return '';
  try {
    const d = new Date(isoDate + 'T12:00:00');
    if (Number.isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return isoDate;
  }
}

const STATUS_LABELS: Record<string, string> = {
  submitted: 'Submitted',
  pending_review: 'Under review',
  shortlisted: 'Shortlisted',
  approved: 'Approved',
  rejected: 'Rejected',
};

const EDITABLE_STATUSES = ['submitted', 'pending_review'];

/** Human-readable labels for primary applicant fields (read-only view). */
const PRIMARY_FIELD_LABELS: Record<string, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone',
  dateOfBirth: 'Date of birth',
  moveInDate: 'Preferred move-in date',
  numberOfOccupants: 'Number of occupants',
  currentAddress: 'Current address',
  timeAtCurrentAddress: 'Time at current address',
  previousAddress: 'Previous address',
  previousAddressIfLessThan2Years: 'Previous address (if less than 2 years)',
  employment: 'Employment',
  employmentStatus: 'Employment status',
  employer: 'Employer',
  employerName: 'Employer name',
  position: 'Position',
  lengthOfEmployment: 'Length of employment',
  employerContact: 'Employer contact',
  income: 'Income',
  incomeFrequency: 'Income frequency',
  netWeeklyIncome: 'Net weekly income',
  businessName: 'Business name',
  abn: 'ABN',
  accountantContact: 'Accountant contact',
  idType: 'ID type',
  idNumber: 'ID number',
  idState: 'ID state',
  idCountry: 'ID country',
  idStateCountry: 'ID state / country',
  notes: 'Notes',
  priorRentalHistory: 'Prior rental history',
  outstandingArrears: 'Outstanding rental arrears',
  tenancyDatabaseListed: 'Listed on tenancy database',
  tribunalTermination: 'Tenancy terminated by tribunal',
  financialDisclosureDetails: 'Financial disclosure details',
};

function formatFieldValue(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  return String(value).trim();
}

function formatFieldLabel(key: string): string {
  return PRIMARY_FIELD_LABELS[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).replace(/_/g, ' ');
}

const RentalApplicationView: React.FC = () => {
  const { applicantToken } = useParams<{ applicantToken: string }>();
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<{ summary: any; application: RentalApplicationSummary } | null>(null);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, string | number>>({});
  const [editSuccess, setEditSuccess] = useState(false);

  const handleUnlock = async () => {
    if (!applicantToken || !accessCode || accessCode.length !== 6) {
      setError('Please enter a valid 6-digit access code');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await getApplicationByApplicantToken(applicantToken, accessCode);
      setData(result);
    } catch (e: any) {
      setError(e?.message || 'Invalid access code');
    } finally {
      setLoading(false);
    }
  };

  const canEdit = data?.application && EDITABLE_STATUSES.includes(data.application.status);
  const primaryForEdit = data?.application ? (data.application.primaryApplicant || data.application.applicant || {}) : {};
  const editFormFilled = Object.keys(editForm).length > 0 ? editForm : {
    firstName: primaryForEdit.firstName ?? '',
    lastName: primaryForEdit.lastName ?? '',
    email: primaryForEdit.email ?? '',
    phone: primaryForEdit.phone ?? '',
    moveInDate: primaryForEdit.moveInDate ?? '',
    currentAddress: primaryForEdit.currentAddress ?? '',
    notes: primaryForEdit.notes ?? '',
    numberOfOccupants: primaryForEdit.numberOfOccupants ?? '',
    employment: primaryForEdit.employment ?? '',
    employer: primaryForEdit.employer ?? '',
    income: primaryForEdit.income ?? '',
  };

  const handleSaveDetails = async () => {
    if (!applicantToken || !accessCode || accessCode.length !== 6) return;
    setError(null);
    setEditSuccess(false);
    setSaving(true);
    try {
      const primaryApplicant: Record<string, unknown> = {};
      const keys = ['firstName', 'lastName', 'email', 'phone', 'moveInDate', 'currentAddress', 'notes', 'numberOfOccupants', 'employment', 'employer', 'income'];
      keys.forEach((k) => {
        const v = editFormFilled[k];
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          primaryApplicant[k] = k === 'numberOfOccupants' || k === 'income' ? Number(v) : String(v).trim();
        }
      });
      if (primaryApplicant.email) primaryApplicant.email = String(primaryApplicant.email).toLowerCase();
      const result = await updateApplicationByApplicantToken(applicantToken, accessCode, primaryApplicant);
      setData(result);
      setEditSuccess(true);
      setTimeout(() => setEditSuccess(false), 3000);
    } catch (e: any) {
      setError(e?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (!applicantToken) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">Invalid link</Alert>
        <Typography variant="body2" sx={{ mt: 2 }}>
          <Link component={RouterLink} to="/">Return home</Link>
        </Typography>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom align="center">
            View your application
          </Typography>
          <Divider sx={{ my: 2 }} />
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the 6-digit access code you received when you submitted your application.
          </Typography>
          <TextField
            fullWidth
            label="Access code"
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
            sx={{ mb: 2 }}
          />
          <Button
            fullWidth
            variant="contained"
            onClick={handleUnlock}
            disabled={loading || accessCode.length !== 6}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'View application'}
          </Button>
        </Paper>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          <Link component={RouterLink} to="/">Return home</Link>
        </Typography>
      </Container>
    );
  }

  const { summary, application } = data;
  const primary = application.primaryApplicant || application.applicant || {};
  const statusLabel = STATUS_LABELS[application.status] || application.status;

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Your rental application
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {summary?.address}
        {summary?.dateAvailable && ` • Available from ${formatDisplayDate(summary.dateAvailable)}`}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight="600">Status</Typography>
          <Chip
            label={statusLabel}
            color={
              application.status === 'approved' ? 'success' :
              application.status === 'rejected' ? 'error' :
              application.status === 'shortlisted' ? 'primary' : 'default'
            }
            size="small"
          />
        </Box>
        {application.submittedAt && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Submitted {formatDisplayDate(typeof application.submittedAt === 'string' ? application.submittedAt.split('T')[0] : application.submittedAt)}
          </Typography>
        )}
        {application.outcomeNotes && (
          <Typography variant="body2" sx={{ mt: 1.5 }}>{application.outcomeNotes}</Typography>
        )}
      </Paper>

      <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>Application details</Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>Primary applicant</Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {(() => {
            const primary = application.primaryApplicant || application.applicant || {};
            const skipKeys = new Set(['extra', '_id']);
            const entries = Object.entries(primary).filter(([k, v]) => !skipKeys.has(k) && formatFieldValue(v) !== '');
            return entries.map(([key, value]) => (
              <Grid item xs={12} sm={6} key={key}>
                <Typography variant="caption" color="text.secondary" display="block">{formatFieldLabel(key)}</Typography>
                <Typography variant="body2">{formatFieldValue(value)}</Typography>
              </Grid>
            ));
          })()}
          {(() => {
            const primary = application.primaryApplicant || application.applicant || {};
            const extra = primary?.extra && typeof primary.extra === 'object' ? primary.extra as Record<string, unknown> : {};
            return Object.entries(extra).map(([key, value]) => {
              const str = formatFieldValue(value);
              if (str === '') return null;
              return (
                <Grid item xs={12} sm={6} key={`extra-${key}`}>
                  <Typography variant="caption" color="text.secondary" display="block">{formatFieldLabel(key)}</Typography>
                  <Typography variant="body2">{str}</Typography>
                </Grid>
              );
            });
          })()}
        </Grid>

        {Array.isArray(application.coApplicants) && application.coApplicants.length > 0 && (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>Co-applicants</Typography>
            {application.coApplicants.map((co: any, i: number) => (
              <Box key={i} sx={{ mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                <Typography variant="body2" fontWeight="600">
                  {[co.firstName, co.lastName].filter(Boolean).join(' ')}
                </Typography>
                {co.email && <Typography variant="body2" color="text.secondary">{co.email}</Typography>}
                {co.phone && <Typography variant="body2" color="text.secondary">{co.phone}</Typography>}
                {co.dateOfBirth && <Typography variant="body2" color="text.secondary">DOB: {formatFieldValue(co.dateOfBirth)}</Typography>}
                {co.currentAddress && <Typography variant="body2" color="text.secondary">{co.currentAddress}</Typography>}
                {co.relationshipToPrimary && <Typography variant="body2" color="text.secondary">Relationship: {co.relationshipToPrimary}</Typography>}
              </Box>
            ))}
          </>
        )}

        {Array.isArray(application.childOccupants) && application.childOccupants.length > 0 && (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>Child occupants</Typography>
            <List dense disablePadding>
              {application.childOccupants.map((child: any, i: number) => (
                <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary={child.fullName}
                    secondary={child.dateOfBirth ? `DOB: ${formatFieldValue(child.dateOfBirth)}` : undefined}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Paper>

      {canEdit && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Update your details</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You can update your contact and application details while your application is submitted or under review.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="First name" value={editFormFilled.firstName ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, firstName: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Last name" value={editFormFilled.lastName ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="email" label="Email" value={editFormFilled.email ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone" value={editFormFilled.phone ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Preferred move-in date" value={editFormFilled.moveInDate ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, moveInDate: e.target.value }))} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Number of occupants" value={editFormFilled.numberOfOccupants ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, numberOfOccupants: e.target.value }))} size="small" inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Current address" value={editFormFilled.currentAddress ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, currentAddress: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Employment" value={editFormFilled.employment ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, employment: e.target.value }))} size="small" placeholder="e.g. Full-time" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Employer" value={editFormFilled.employer ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, employer: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Income" value={editFormFilled.income ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, income: e.target.value }))} size="small" inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Notes" value={editFormFilled.notes ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={handleSaveDetails} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
              {editSuccess && <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>Details updated successfully.</Typography>}
            </Grid>
          </Grid>
        </Paper>
      )}

      {Array.isArray(application.documents) && application.documents.length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>Documents</Typography>
          <List dense component={Paper} variant="outlined" sx={{ mb: 2 }}>
            {application.documents.map((doc: any, i: number) => (
              <ListItem key={i} component={doc.url ? Link : 'div'} href={doc.url} target="_blank" rel="noopener">
                <ListItemText primary={doc.name || `Document ${i + 1}`} secondary={doc.documentType} />
              </ListItem>
            ))}
          </List>
        </>
      )}

      {application.tenancyAgreement?.url && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1 }}>Tenancy agreement</Typography>
          <Link href={application.tenancyAgreement.url} target="_blank" rel="noopener" variant="body2">
            Download tenancy agreement
          </Link>
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        Questions? Contact the agent managing this property.
      </Typography>
    </Container>
  );
};

export default RentalApplicationView;
